import { Injectable } from '@angular/core';
import { snakeCase } from 'lodash';
import { ButtonTappedTrackingAction } from './models/actions/button-tapped-tracking-action.model';
import { SignInStartedTrackingAction } from './models/actions/sign-in-started-tracking-action.model';
import { SignInCompletedTrackingAction } from './models/actions/sign-in-completed-tracking-action.model';
import { SignInFailedTrackingAction } from './models/actions/sign-in-failed-tracking-action.model';
import { SignUpStartedTrackingAction } from './models/actions/sign-up-started-tracking-action.model';
import { SignUpCompletedTrackingAction } from './models/actions/sign-up-completed-tracking-action.model';
import { SignUpFailedTrackingAction } from './models/actions/sign-up-failed-tracking-action.model';
import { PresentedTrackingAction } from './models/actions/presented-tracking-action.model';
import { CheckboxCheckedTrackingAction } from './models/actions/checkbox-checked-tracking-action.model';
import { CheckboxUncheckedTrackingAction } from './models/actions/checkbox-unchecked-tracking-action.model';
import { RadioTappedTrackingAction } from './models/actions/radio-selected-tracking-action.model';
import { ErrorInvokedTrackingAction } from './models/actions/error-invoked-tracking-action.model';
import { TappedTrackingAction } from './models/actions/tapped-tracking-action.model';
import { TabTappedTrackingAction } from './models/actions/tab-tapped-tracking-action.model';
import { CardPresentedTrackingAction } from './models/actions/card-presented-tracking-action.model';
import { FailedTrackingAction } from './models/actions/failed-tracking-action.model';
import { SubmittedTrackingAction } from './models/actions/submitted-tracking-action.model';
import { SuccessTrackingAction } from './models/actions/success-tracking-action.model';
import { CellTappedTrackingAction } from './models/actions/cell-tapped-tracking-action.model';
import { DeletedTrackingAction } from './models/actions/deleted-tracking-action.model';
import { DropdownSelectedTrackingAction } from './models/actions/dropdown-selected-tracking-action.model';
import { DropdownTappedTrackingAction } from './models/actions/dropdown-tapped-tracking-action.model';
import { InputTappedTrackingAction } from './models/actions/input-tapped-tracking-action.model';
import { InputAssignedTrackingAction } from './models/actions/input-assigned-tracking-action.model';
import { InputAssignedBulkTrackingAction } from './models/actions/input-assigned-bulk-tracking-action.model';
import { InputUnassignedTrackingAction } from './models/actions/input-unassigned-tracking-action.model';
import { OpenedTrackingAction } from './models/actions/opened-tracking-action.model';
import { ReportPresentedTrackingAction } from './models/actions/report-presented-tracking-action.model';
import { ResultTappedTrackingAction } from './models/actions/result-tapped-tracking-action.model';
import { SavedTrackingAction } from './models/actions/saved-tracking-action.model';
import { SearchedTrackingAction } from './models/actions/searched-tracking-action.model';
import { SignedOutTrackingAction } from './models/actions/signed-out-tracking-action.model';
import { ToggleTappedTrackingAction } from './models/actions/toggle-tapped-tracking-action.model';
import { TrialPurchasedTrackingAction } from './models/actions/trial-purchased-tracking-action.model';
import { UploadCompletedTrackingAction } from './models/actions/upload-completed-tracking-action.model';
import { UploadFailedTrackingAction } from './models/actions/upload-failed-tracking-action.model';
import { AppLoadedTrackingAction } from './models/actions/app-loaded-tracking-action.model';
import { ErrorTrackingAction } from './models/actions/error-tracking-action.model';
import { RenderedTrackingAction } from './models/actions/page-rendered-tracking-action.model';
import { FieldTappedTrackingAction } from './models/actions/field-tapped-tracking-action.model';
import { CardFormChangedTrackingAction } from './models/actions/card-form-changed-tracking-action.model';
import { InputChangedTrackingAction } from './models/actions/input-changed-tracking-action.model';
import { ChartTappedTrackingAction } from './models/actions/chart-tapped-tracking-action.model';
import {
  ScrollDirectionEnum,
  ScrolledTrackingAction,
} from './models/actions/scrolled-tracking-action.model';
import { ITrackingAction, TrackingActionNamesEnum } from './models';

@Injectable()
export class I2gNgTrackingActionBuilder {
  buildAppLoaded(
    timeToFirstPaint: number,
    timeToAppLoad: number,
    timeToInteractive: number
  ): AppLoadedTrackingAction {
    const trackingKeys = AppLoadedTrackingAction.KEYS;

    return new AppLoadedTrackingAction({
      [trackingKeys.TIME_TO_FIRST_PAINT]: timeToFirstPaint,
      [trackingKeys.TIME_TO_APP_LOAD]: timeToAppLoad,
      [trackingKeys.TIME_TO_INTERACTIVE]: timeToInteractive,
    });
  }

  /**
   * Build button tapped tracking action
   *
   * @param identifier Unique identifier for the button
   * @param screenName Tracking Screen Name
   */
  buildButtonTapped(identifier: string, screenName: string): ButtonTappedTrackingAction {
    const trackingKeys = ButtonTappedTrackingAction.KEYS;

    return new ButtonTappedTrackingAction({
      [trackingKeys.BUTTON_IDENTIFIER]: identifier,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Card presented tracking action
   *
   * @param screenName Tracking screen name
   */
  buildCardPresented(screenName: string): CardPresentedTrackingAction {
    const trackingKeys = CardPresentedTrackingAction.KEYS;

    return new CardPresentedTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  buildCellTapped(screenName: string): CellTappedTrackingAction {
    const trackingKeys = CellTappedTrackingAction.KEYS;

    return new CellTappedTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Checkbox checked tracking action
   *
   * @param identifier Checkbox identifier
   * @param screenName Tracking screen name
   */
  buildCheckboxChecked(identifier: string, screenName: string): CheckboxCheckedTrackingAction {
    const trackingKeys = CheckboxCheckedTrackingAction.KEYS;

    return new CheckboxCheckedTrackingAction({
      [trackingKeys.CHECKBOX_IDENTIFIER]: identifier,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Checkbox unchecked tracking action
   *
   * @param identifier Checkbox identifier
   * @param screenName Tracking screen name
   */
  buildCheckboxUnchecked(identifier: string, screenName: string): CheckboxUncheckedTrackingAction {
    const trackingKeys = CheckboxCheckedTrackingAction.KEYS;

    return new CheckboxUncheckedTrackingAction({
      [trackingKeys.CHECKBOX_IDENTIFIER]: identifier,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  buildDeleted(screenName?: string): DeletedTrackingAction {
    const trackingKeys = DeletedTrackingAction.KEYS;

    return new DeletedTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName ? screenName : '',
    });
  }

  buildDropdownSelected(identifier: string, screenName: string): DropdownSelectedTrackingAction {
    const trackingKeys = DropdownSelectedTrackingAction.KEYS;

    return new DropdownSelectedTrackingAction({
      [trackingKeys.DROPDOWN_IDENTIFIER]: identifier,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  buildDropdownTapped(identifier: string, screenName: string): DropdownTappedTrackingAction {
    const trackingKeys = DropdownTappedTrackingAction.KEYS;

    return new DropdownTappedTrackingAction({
      [trackingKeys.DROPDOWN_IDENTIFIER]: identifier,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  buildError(): ErrorTrackingAction {
    return new ErrorTrackingAction();
  }

  /**
   * Error invoked tracking action
   *
   * @param errorType Type of error
   * @param fieldId form field ID
   * @param screenName Tracking screen name
   */
  buildErrorInvoked(
    errorType: string,
    fieldId: string,
    screenName: string
  ): ErrorInvokedTrackingAction {
    const trackingKeys = ErrorInvokedTrackingAction.KEYS;
    const errorName = snakeCase(`${errorType} ${fieldId}`);

    return new ErrorInvokedTrackingAction({
      [trackingKeys.ERROR_TYPE]: errorName,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Failed tracking action
   *
   * @param screenName Tracking screen name
   */
  buildFailed(screenName: string, errorMessage?: string): FailedTrackingAction {
    const trackingKeys = FailedTrackingAction.KEYS;

    return new FailedTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
      [trackingKeys.ERROR_MESSAGE]: errorMessage,
    });
  }

  buildInputTapped(
    inputId: string,
    inputLocalId: string,
    inputType: string,
    screenName: string
  ): InputTappedTrackingAction {
    const trackingKeys = InputTappedTrackingAction.KEYS;

    return new InputTappedTrackingAction({
      [trackingKeys.INPUT_ID]: inputId,
      [trackingKeys.INPUT_LOCAL_ID]: inputLocalId,
      [trackingKeys.INPUT_TYPE]: inputType,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  buildInputAssigned(
    inputId: string,
    inputLocalId: string,
    inputType: string
  ): InputAssignedTrackingAction {
    const trackingKeys = InputTappedTrackingAction.KEYS;

    return new InputAssignedTrackingAction({
      [trackingKeys.INPUT_ID]: inputId,
      [trackingKeys.INPUT_LOCAL_ID]: inputLocalId,
      [trackingKeys.INPUT_TYPE]: inputType,
    });
  }

  buildInputAssignedBulk(inputType: string, count: number): InputAssignedTrackingAction {
    const trackingKeys = InputAssignedBulkTrackingAction.KEYS;

    return new InputAssignedBulkTrackingAction({
      [trackingKeys.INPUT_TYPE]: inputType,
      [trackingKeys.COUNT]: count,
    });
  }

  buildInputUnassigned(
    inputId: string,
    inputLocalId: string,
    inputType: string
  ): InputUnassignedTrackingAction {
    const trackingKeys = InputTappedTrackingAction.KEYS;

    return new InputUnassignedTrackingAction({
      [trackingKeys.INPUT_ID]: inputId,
      [trackingKeys.INPUT_LOCAL_ID]: inputLocalId,
      [trackingKeys.INPUT_TYPE]: inputType,
    });
  }

  buildInputChanged(): InputChangedTrackingAction {
    return new InputChangedTrackingAction();
  }

  buildOpened(
    previouslyExisted: boolean,
    screenName: string,
    billedStatus?: string
  ): OpenedTrackingAction {
    const trackingKeys = OpenedTrackingAction.KEYS;

    return new OpenedTrackingAction({
      [trackingKeys.PREVIOUSLY_EXISTED]: previouslyExisted,
      [trackingKeys.SCREEN_NAME]: screenName,
      [trackingKeys.BILLED_STATUS]: billedStatus || null,
    });
  }

  buildPageRendered(
    screenName: string,
    webviewLoadTime: number,
    webviewCreateTime: number,
    reachable: boolean,
    failReason: string
  ): RenderedTrackingAction {
    const trackingKeys = RenderedTrackingAction.KEYS;

    return new RenderedTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
      [trackingKeys.WEBVIEW_LOAD_TIME]: webviewLoadTime,
      [trackingKeys.WEBVIEW_CREATE_TIME]: webviewCreateTime,
      [trackingKeys.REACHABLE]: reachable,
      [trackingKeys.FAIL_REASON]: failReason,
    });
  }

  /**
   * Presented tracking action
   *
   * @param screenName Tracking screen name
   */
  buildPresented(screenName: string): PresentedTrackingAction {
    const trackingKeys = PresentedTrackingAction.KEYS;

    return new PresentedTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Radio tapped tracking action
   *
   * @param identifier Radio button identifier
   * @param screenName Tracking screen name
   */
  buildRadioTapped(identifier: string, screenName: string): RadioTappedTrackingAction {
    const trackingKeys = RadioTappedTrackingAction.KEYS;

    return new RadioTappedTrackingAction({
      [trackingKeys.RADIO_IDENTIFIER]: identifier,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  buildReportPresented(reportType: string, screenName: string): ReportPresentedTrackingAction {
    const trackingKeys = ReportPresentedTrackingAction.KEYS;

    return new ReportPresentedTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
      [trackingKeys.REPORT_TYPE]: reportType,
    });
  }

  buildResultTapped(
    resultType: string,
    position: number,
    screenName: string = '',
    searchQueryTerm: string = null
  ): ResultTappedTrackingAction {
    const trackingKeys = ResultTappedTrackingAction.KEYS;

    return new ResultTappedTrackingAction({
      [trackingKeys.RESULT_TYPE]: resultType,
      [trackingKeys.LIST_POSITION]: position,
      [trackingKeys.SEARCH_QUERY_TERM]: searchQueryTerm,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  buildSaved(previouslyExisted: boolean): SavedTrackingAction {
    const trackingKeys = SavedTrackingAction.KEYS;

    return new SavedTrackingAction({
      [trackingKeys.PREVIOUSLY_EXISTED]: previouslyExisted,
    });
  }

  buildSearched(
    searchQueryTerm: string,
    numOfResults: number,
    screenName: string
  ): SearchedTrackingAction {
    const trackingKeys = SearchedTrackingAction.KEYS;

    return new SearchedTrackingAction({
      [trackingKeys.SEARCH_QUERY_TERM]: searchQueryTerm,
      [trackingKeys.NUM_RESULTS_RETURNED]: numOfResults,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Sign in started tracking action, after the button tapped happens,
   * this event fires (somewhat a duplicate of the button_tap) specifically
   * for the starting of the sign in process.
   *
   * @param screenName Tracking Screen Name
   */
  buildSignInStarted(screenName: string): SignInStartedTrackingAction {
    const trackingKeys = SignInStartedTrackingAction.KEYS;

    return new SignInStartedTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Sign in completed tracking action.
   *
   * @param screenName Tracking Screen Name
   */
  buildSignInCompleted(screenName: string): SignInCompletedTrackingAction {
    const trackingKeys = SignInCompletedTrackingAction.KEYS;

    return new SignInCompletedTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Sign in failed tracking action
   *
   * @param errorCode Error code from OAuth server
   * @param screenName Tracking screen name
   */
  buildSignInFailed(errorCode: string, screenName: string): SignInFailedTrackingAction {
    const trackingKeys = SignInFailedTrackingAction.KEYS;

    return new SignInFailedTrackingAction({
      [trackingKeys.ERROR_CODE]: errorCode,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  buildSignedOut(screenName: string): SignedOutTrackingAction {
    const trackingKeys = SignedOutTrackingAction.KEYS;

    return new SignedOutTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Sign up started tracking action, after the button tapped happens,
   * this event fires (somewhat a duplicate of the button_tap) specifically
   * for the starting of the sign up process.
   *
   * @param screenName Tracking Screen Name
   */
  buildSignUpStarted(screenName: string): SignUpStartedTrackingAction {
    const trackingKeys = SignUpStartedTrackingAction.KEYS;

    return new SignUpStartedTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Sign up completed tracking action.
   *
   * @param screenName Tracking Screen Name
   */
  buildSignUpCompleted(screenName: string): SignUpCompletedTrackingAction {
    const trackingKeys = SignUpCompletedTrackingAction.KEYS;

    return new SignUpCompletedTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Sign up failed tracking action
   *
   * @param errorCode Error code from OAuth server
   * @param screenName Tracking screen name
   */
  buildSignUpFailed(errorCode: string, screenName: string): SignUpFailedTrackingAction {
    const trackingKeys = SignUpFailedTrackingAction.KEYS;

    return new SignUpFailedTrackingAction({
      [trackingKeys.ERROR_CODE]: errorCode,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  buildToggleTapped(
    identifier: string,
    value: boolean,
    screenName: string
  ): ToggleTappedTrackingAction {
    const trackingKeys = ToggleTappedTrackingAction.KEYS;

    return new ToggleTappedTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
      [trackingKeys.TOGGLE_IDENTIFIER]: identifier,
      [trackingKeys.TOGGLE_VALUE]: value,
    });
  }

  buildTrialPurchased(screenName: string): TrialPurchasedTrackingAction {
    const trackingKeys = TappedTrackingAction.KEYS;

    return new TrialPurchasedTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Build tapped tracking action
   *
   * @param screenName Tracking screen name
   */
  buildTapped(screenName: string): TappedTrackingAction {
    const trackingKeys = TappedTrackingAction.KEYS;

    return new TappedTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Build tab tapped tracking action
   *
   * @param identifier Unique identifier for the tab
   * @param screenName Tracking Screen Name
   */
  buildTabTapped(identifier: string, screenName: string): TabTappedTrackingAction {
    const trackingKeys = TabTappedTrackingAction.KEYS;

    return new TabTappedTrackingAction({
      [trackingKeys.TAB_IDENTIFIER]: identifier,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  buildUploadCompleted(
    inputId: string,
    inputLocalId: string,
    inputType: string
  ): UploadCompletedTrackingAction {
    const trackingKeys = UploadCompletedTrackingAction.KEYS;

    return new UploadCompletedTrackingAction({
      [trackingKeys.INPUT_ID]: inputId,
      [trackingKeys.INPUT_LOCAL_ID]: inputLocalId,
      [trackingKeys.INPUT_TYPE]: inputType,
    });
  }

  buildUploadFailed(
    inputId: string,
    inputLocalId: string,
    inputType: string
  ): UploadFailedTrackingAction {
    const trackingKeys = UploadFailedTrackingAction.KEYS;

    return new UploadFailedTrackingAction({
      [trackingKeys.INPUT_ID]: inputId,
      [trackingKeys.INPUT_LOCAL_ID]: inputLocalId,
      [trackingKeys.INPUT_TYPE]: inputType,
    });
  }

  /**
   * Submitted tracking action
   *
   * @param screenName Tracking screen name
   */
  buildSubmitted(screenName: string): SubmittedTrackingAction {
    const trackingKeys = SubmittedTrackingAction.KEYS;

    return new SubmittedTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Success tracking action
   *
   * @param screenName Tracking screen name
   */
  buildSuccess(screenName: string): SuccessTrackingAction {
    const trackingKeys = SuccessTrackingAction.KEYS;

    return new SuccessTrackingAction({
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Track an input field tap
   *
   * @param fieldIdentifier Input field identifier
   * @param screenName Tracking screen name
   */
  buildFieldTapped(fieldIdentifier: string, screenName: string): FieldTappedTrackingAction {
    const trackingKeys = FieldTappedTrackingAction.KEYS;

    return new FieldTappedTrackingAction({
      [trackingKeys.FIELD_IDENTIFIER]: fieldIdentifier,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Track changes in card form inputs
   *
   * @param empty Indicates if the form is empty
   * @param completed Indicates if the form has been completed
   * @param error Form validation error message / code
   */
  buildCardFormChanged(
    empty: boolean,
    completed: boolean,
    error: string
  ): CardFormChangedTrackingAction {
    const trackingKeys = CardFormChangedTrackingAction.KEYS;

    return new CardFormChangedTrackingAction({
      [trackingKeys.CARD_FORM_EMPTY]: empty,
      [trackingKeys.CARD_FORM_COMPLETED]: completed,
      [trackingKeys.CARD_FORM_VALIDATION_ERROR]: error,
    });
  }

  /**
   * Track chart tapped
   *
   * @param chartIdentifier Unique identifier for chart
   * @param screenName Tracking screen name
   */
  buildChartTapped(chartIdentifier: string, screenName: string): ChartTappedTrackingAction {
    const trackingKeys = ChartTappedTrackingAction.KEYS;

    return new ChartTappedTrackingAction({
      [trackingKeys.CHART_IDENTIFIER]: chartIdentifier,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  /**
   * Track page scroll
   *
   * @param scrollDirection Direction of scroll
   * @param screenName Tracking screen name
   */
  buildScrolled(scrollDirection: ScrollDirectionEnum, screenName: string): ScrolledTrackingAction {
    const trackingKeys = ScrolledTrackingAction.KEYS;

    return new ScrolledTrackingAction({
      [trackingKeys.SCROLL_DIRECTION]: scrollDirection,
      [trackingKeys.SCREEN_NAME]: screenName,
    });
  }

  buildStarted(): ITrackingAction {
    return { name: TrackingActionNamesEnum.started, properties: {} };
  }

  buildCompleted(): ITrackingAction {
    return { name: TrackingActionNamesEnum.completed, properties: {} };
  }

  buildViewed(): ITrackingAction {
    return {
      name: TrackingActionNamesEnum.viewed,
      properties: {},
    };
  }
}
